import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    List,
    ListItem,
    ListItemText,
    Link,
    Switch,
    Select,
    MenuItem,
    Typography,
} from "@mui/material/";
import { useTranslation } from "react-i18next";
import makeStyles from "@mui/styles/makeStyles";
import { useDispatch, useSelector } from "react-redux";
import actions from "../Store/actions";
import styles from "./styles.js";
import config from "../config";
import { useBranding, useToaster } from "../common/hooks";
import { Copyright } from "../common/components";
import SettingsDialog from "./SettingsDialog";
import IndexDBDump from "./Sections/IndexDBDump";
import { logToSentry } from "../config/Sentry";
import { db } from "../common/lib";
import { update } from "idb-keyval";

const Settings = () => {
    const { t, i18n } = useTranslation();
    const classes = makeStyles(styles)();
    const { version } = config;

    const { themes, supportUrl, privacyPolicy } = useBranding();

    const dispatch = useDispatch();
    const toast = useToaster();

    const { app, appSettings, organisation, auth } = useSelector(
        (state) => state
    );
    const { language } = auth.account;
    const { name } = organisation;
    const sync = app.backgroundSyncEnabled;
    const imageSync = app.imageSyncEnabled;
    const camera = appSettings.cameraSource;
    const cvSelectedTheme = localStorage.getItem("cvSelectedTheme");
    const cvSelectedLang = localStorage.getItem("cvAuditorLang");
    const selectedTheme = cvSelectedTheme ? cvSelectedTheme : null;

    const privacyPath = `${privacyPolicy}`;

    const [themeName, setThemeName] = useState("org");
    const [lang, setLang] = useState("");
    const [versionTapCounter, setVersionTapCounter] = useState(0);
    const [open, setOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogMessage, setDialogMessage] = useState("");
    const [dialogTrigger, setDialogTrigger] = useState("");

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    useEffect(() => {
        const selectedLang = cvSelectedLang ? cvSelectedLang : language;
        if (selectedLang) {
            setLang(selectedLang);
        }
    }, [auth, cvSelectedLang]);

    useEffect(() => {
        if (selectedTheme) {
            setThemeName(selectedTheme);
        }
    }, [selectedTheme]);

    const onBackgroundSyncPress = () => {
        try {
            dispatch(
                actions.app.create.update({
                    backgroundSyncEnabled: !app.backgroundSyncEnabled,
                })
            );
        } catch (error) {
            console.error(error);
            logToSentry("onBackgroundSyncPress", { error });
            toast.error(t("errors.backgroundSync"));
        }
    };

    const onImageSyncPress = () => {
        try {
            dispatch(
                actions.app.create.update({
                    imageSyncEnabled: !app.imageSyncEnabled,
                })
            );
        } catch (error) {
            console.error(error);
            toast.error(t("errors.backgroundSync"));
            logToSentry("onImageSyncPress", { error });
        }
    };

    const onCameraSwitch = () => {
        try {
            dispatch(
                actions.appSettings.create.set({
                    cameraSource:
                        appSettings.cameraSource === "native"
                            ? "browser"
                            : "native",
                })
            );
        } catch (error) {
            console.error(error);
            toast.error(t("errors.cameraSource"));
            logToSentry("onCameraSwitch", { error });
        }
    };

    const onThemeChange = (event) => {
        const value = event.target.value;
        setThemeName(value);

        dispatch(
            actions.appSettings.create.set({
                themeName: value,
            })
        );
        localStorage.setItem("cvSelectedTheme", value);
    };

    const onLangaugeChange = async (event) => {
        const value = event.target.value;
        setLang(value);
        i18n.changeLanguage(value);
        await dispatch(
            actions.appSettings.create.set({
                language: value,
            })
        );

        const detail = {
            userId: auth.account.id,
            languageCode: value,
        };

        await dispatch(actions.userLanguage.create.postRequest(detail));
        localStorage.setItem("cvAuditorLang", value);
        window.location.reload();
    };

    const hdlVersionTap = () => setVersionTapCounter(versionTapCounter + 1);

    const hdlClearCacheClick = () => {
        setDialogTrigger("clearCache");
        setDialogTitle(t("clearCacheTitle"));
        setDialogMessage(t("messages.clearCacheMessage"));
        handleClickOpen();
    };

    const hdlClearIDBClick = () => {
        setDialogTrigger("clearIDB");
        setDialogTitle(t("removeIDBDataTitle"));
        setDialogMessage(t("messages.clearIDBMessage"));
        handleClickOpen();
    };

    const confirmClearCache = (hasIdbCleared) => {
        dispatch(actions.altAssets.create.clear());
        dispatch(actions.assetAuditors.create.clear());
        dispatch(actions.auditAssets.create.clear());
        dispatch(actions.auditAssignments.create.clear());
        dispatch(actions.auditCompletion.create.clear());
        dispatch(actions.auditLocations.create.clear());
        dispatch(actions.audits.create.clear());
        dispatch(actions.auditUpload.create.clear());
        dispatch(actions.typesList.create.clear());
        dispatch(actions.search.create.clear());

        update(db.types.reduxState, () => []);

        console.log("CACHE CLEARED");
        toast.info(t("messages.clearCacheSuccess"), 2000);
        handleClose();
    };

    const confirmClearIDB = () => {
        update(db.types.auditAsset, () => []);
        update(db.types.assetAuditor, () => []);
        update(db.types.auditAssignment, () => []);
        update(db.types.altAsset, () => []);
        update(db.types.pendingSubmission, () => []);
        update(db.types.reduxState, () => []);
        update(db.types.searchCache, () => []);
        update(db.types.typesList, () => []);

        console.log("IDB CLEARED");
        toast.info(t("messages.clearIDBSuccess"), 2000);
        handleClose();
    };

    return (
        <Box sx={{ height: "85vh", overflow: "scroll" }}>
            <List className={classes.settingsListContainer}>
                <ListItem className={classes.listItem} key={`backgroundSync`}>
                    <ListItemText
                        primary={
                            <Typography color="inherit">
                                {t("backgroundSync")}
                            </Typography>
                        }
                    />
                    <Switch
                        edge="end"
                        checked={sync}
                        onChange={onBackgroundSyncPress}
                        inputProps={{ "aria-labelledby": "backgroundSync" }}
                    />
                </ListItem>
                <ListItem className={classes.listItem} key={`imageSync`}>
                    <ListItemText
                        primary={
                            <Typography color="inherit">
                                {t("imageSync")}
                            </Typography>
                        }
                    />
                    <Switch
                        edge="end"
                        checked={imageSync}
                        onChange={onImageSyncPress}
                        inputProps={{ "aria-labelledby": "imageSync" }}
                    />
                </ListItem>
                <ListItem className={classes.listItem} key={`cameraSource`}>
                    <ListItemText
                        primary={
                            <Typography color="inherit">
                                {t("cameraSource")}
                            </Typography>
                        }
                    />
                    <Switch
                        edge="end"
                        checked={camera !== "browser"}
                        onChange={onCameraSwitch}
                        inputProps={{ "aria-labelledby": "cameraSource" }}
                    />
                </ListItem>
                <ListItem className={classes.listItem} key={`theme_item`}>
                    <ListItemText
                        primary={
                            <Typography color="inherit">
                                {t("theme")}
                            </Typography>
                        }
                    />
                    <Select
                        labelId="theme-select"
                        id="theme-select"
                        value={
                            !themes && themeName === "org"
                                ? "original"
                                : themeName
                        }
                        label="theme"
                        name="theme"
                        variant="standard"
                        onChange={onThemeChange}
                    >
                        <MenuItem value="neutral">{t("neutral")}</MenuItem>
                        {themes ? (
                            <MenuItem value="org">{name}</MenuItem>
                        ) : (
                            <MenuItem value="original">
                                {t("original")}
                            </MenuItem>
                        )}
                    </Select>
                </ListItem>
                <ListItem className={classes.listItem} key={`language_item`}>
                    <ListItemText
                        primary={
                            <Typography color="inherit">
                                {t("language")}
                            </Typography>
                        }
                    />
                    <Select
                        labelId="language-select"
                        id="langauge-select"
                        value={lang}
                        label={t("langauge")}
                        name="language"
                        variant="standard"
                        onChange={onLangaugeChange}
                    >
                        <MenuItem value="en">{t("english")}</MenuItem>
                        <MenuItem value="fr">{t("french")}</MenuItem>
                        <MenuItem value="es">{t("spanish")}</MenuItem>
                        <MenuItem value="it">{t("italian")}</MenuItem>
                    </Select>
                </ListItem>
                <ListItem className={classes.listItem} key={`support_item`}>
                    <ListItemText
                        primary={
                            <Link
                                component="a"
                                color="inherit"
                                rel="noopener noreferrer"
                                href={
                                    organisation.isInternalSupport
                                        ? config.paths.contactForm
                                        : supportUrl
                                }
                                target={
                                    organisation.isInternalSupport
                                        ? "_self"
                                        : "_blank"
                                }
                                underline={"always"}
                            >
                                {t("support")}
                            </Link>
                        }
                    />
                </ListItem>
                <ListItem
                    className={classes.listItem}
                    key={`privacyPolicy_item`}
                >
                    <ListItemText
                        primary={
                            <Link
                                component="a"
                                color="inherit"
                                rel="noopener noreferrer"
                                href={privacyPath}
                                target={"_blank"}
                                underline={"always"}
                            >
                                {t("privacyPolicy")}
                            </Link>
                        }
                    />
                </ListItem>
                <ListItem
                    className={classes.listItem}
                    key={`version_item`}
                    secondaryAction={
                        <Typography type="body1" onClick={hdlVersionTap}>
                            {version}
                        </Typography>
                    }
                >
                    <ListItemText
                        primary={
                            <Typography color="inherit">
                                {t("version")}
                            </Typography>
                        }
                    />
                </ListItem>
                <IndexDBDump />
            </List>
            <Box sx={{ background: "#ffe5cb", padding: "1rem" }}>
                <Typography type="body1" variant="h5">
                    {t("dangerZone")}
                </Typography>
                <List>
                    <ListItem
                        className={classes.listItem}
                        key={`clearCache_item`}
                        // secondaryAction={
                        //     <Button
                        //         variant="outlined"
                        //         onClick={hdlClearCacheClick}
                        //         color="error"
                        //     >
                        //         {t("clearCache")}
                        //     </Button>
                        // }
                    >
                        <ListItemText
                            primary={
                                <Typography type="body1">
                                    {t("clearCacheTitle")}
                                </Typography>
                            }
                        />
                        <ListItemText
                            secondary={
                                <Button
                                    variant="outlined"
                                    onClick={hdlClearCacheClick}
                                    color="error"
                                >
                                    {t("clearCache")}
                                </Button>
                            }
                        />
                    </ListItem>
                    {versionTapCounter > 5 && (
                        <ListItem
                            className={classes.listItem}
                            key={`clearIDB_item`}
                            // secondaryAction={
                            //}
                        >
                            <ListItemText
                                primary={
                                    <Typography type="body1">
                                        {t("removeIDBDataTitle")}
                                    </Typography>
                                }
                            />
                            <ListItemText
                                primary={
                                    <Button
                                        variant="outlined"
                                        onClick={hdlClearIDBClick}
                                        color="error"
                                    >
                                        {t("clearIDB")}
                                    </Button>
                                }
                            />
                        </ListItem>
                    )}
                </List>
                <SettingsDialog
                    open={open}
                    title={dialogTitle}
                    message={dialogMessage}
                    handleClose={handleClose}
                    handleSuccess={
                        dialogTrigger === "clearCache"
                            ? confirmClearCache
                            : confirmClearIDB
                    }
                />
            </Box>
            {process.env.tenant === "cv" && <Copyright />}
        </Box>
    );
};

export default Settings;
